/**
 * InterviewModeSelector Component
 *
 * Radio group with two options: Online, In-Person
 * Each option has an icon and description
 * When Online is selected, can render additional content inside the card
 */

import React from "react";
import { Radio, Card, Typography, Flex, Divider } from "antd";
import { VideoCameraOutlined, EnvironmentOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface InterviewModeSelectorProps {
  value?: "online" | "in-person";
  onChange?: (value: "online" | "in-person") => void;
  disabled?: boolean;
  onlineCardContent?: React.ReactNode; // Content to render inside Online card when selected
}

const InterviewModeSelector: React.FC<InterviewModeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  onlineCardContent,
}) => {
  const handleChange = (e: any) => {
    onChange?.(e.target.value);
  };

  const isOnlineSelected = value === "online";

  return (
    <Radio.Group
      value={value}
      onChange={handleChange}
      style={{ width: "100%" }}
      disabled={disabled}
    >
      <Flex vertical gap={12} style={{ width: "100%" }}>
        {/* Online Option */}
        <Card
          hoverable={!isOnlineSelected}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            borderColor: isOnlineSelected
              ? "var(--ant-color-primary)"
              : undefined,
            borderWidth: isOnlineSelected ? 2 : 1,
          }}
        >
          <Radio value="online">
            <Flex align="center" gap={12} style={{ width: "100%" }}>
              <VideoCameraOutlined
                style={{ fontSize: 20, color: "var(--ant-color-primary)" }}
              />
              <div style={{ flex: 1 }}>
                <Text strong>Online Interview</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Candidates can schedule meeting with available interviewers
                </Text>
              </div>
            </Flex>
          </Radio>

          {/* Render additional content inside Online card when selected */}
          {isOnlineSelected && onlineCardContent && (
            <>
              <Divider style={{ margin: "16px 0" }} />
              <div onClick={(e) => e.stopPropagation()}>
                {onlineCardContent}
              </div>
            </>
          )}
        </Card>

        {/* Manual Coordination Option */}
        <Card
          hoverable
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        >
          <Radio value="in-person">
            <Flex align="center" gap={12} style={{ width: "100%" }}>
              <EnvironmentOutlined
                style={{ fontSize: 20, color: "var(--ant-color-primary)" }}
              />
              <div>
                <Text strong>Team Will Contact</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Our team will get in touch with candidate for further
                  discussions or interview rounds
                </Text>
              </div>
            </Flex>
          </Radio>
        </Card>
      </Flex>
    </Radio.Group>
  );
};

export default InterviewModeSelector;
